import { create } from 'zustand';
import { trackTourStepCompletion } from '../utils/track-editor-tour';
import { setOnboardingSeenInLocalStorage } from '../data-handler/EditorTourDataHandler';

export const TourItemKey = {
  WRITE_QUESTION: 'write-question',
  EDIT_CONTENT: 'edit-content',
  EDIT_OPTIONS: 'edit-options',
  PRESS_PRESENT: 'press-present',
  QR_CODE: 'qr-code',
  NAVIGATION_ARROWS: 'navigation-arrows',
} as const;

export const TOUR_LENGTH = Object.keys(TourItemKey).length;

// Use the object values as a type
export type TourItemKey = (typeof TourItemKey)[keyof typeof TourItemKey];

export interface TourItemState {
  key: TourItemKey;
  isActive: boolean;
}

export type UpdateItemFunction = (
  key: TourItemKey,
  updates: Partial<TourItemState>,
) => void;

interface TourState {
  isActive: boolean;
  items: TourItemState[];
  shouldShowFinishedModal: boolean;
  updateItem: UpdateItemFunction;
  progressTour: () => void;
  dismissFinishedModal: () => void;
  isItemActive: (key: TourItemKey) => boolean;
}

const defaultItems: TourItemState[] = [
  { key: TourItemKey.WRITE_QUESTION, isActive: false },
  { key: TourItemKey.EDIT_CONTENT, isActive: false },
  { key: TourItemKey.EDIT_OPTIONS, isActive: false },
  { key: TourItemKey.PRESS_PRESENT, isActive: false },
  { key: TourItemKey.QR_CODE, isActive: false },
  { key: TourItemKey.NAVIGATION_ARROWS, isActive: false },
];

export const useTourStore = create<TourState>((set, get) => ({
  isActive: false,
  items: defaultItems,
  shouldShowFinishedModal: false,

  updateItem: (key: TourItemKey, updates: Partial<TourItemState>) => {
    set((state) => ({
      items: state.items.map((item) =>
        item.key === key ? { ...item, ...updates } : item,
      ),
    }));
  },

  progressTour: () => {
    const state = get();
    const currentActiveIndex = state.items.findIndex((item) => item.isActive);

    if (currentActiveIndex === -1) {
      // No active item found, activate the first one
      set({ isActive: true });
      if (state.items.length > 0 && state.items[0]) {
        get().updateItem(state.items[0].key, { isActive: true });
      }
      return;
    }

    // Deactivate current item and track completion
    const currentItem = state.items[currentActiveIndex];
    if (currentItem) {
      get().updateItem(currentItem.key, { isActive: false });
      trackTourStepCompletion(currentItem.key);
    }

    // Activate next item if it exists
    const nextIndex = currentActiveIndex + 1;
    const nextItem = state.items[nextIndex];
    if (nextItem) {
      get().updateItem(nextItem.key, { isActive: true });
    } else {
      // Reached the end of tour, deactivate the tour and show finished modal
      setOnboardingSeenInLocalStorage();
      set({ isActive: false, shouldShowFinishedModal: true });
    }
  },

  dismissFinishedModal: () => {
    set({ shouldShowFinishedModal: false });
  },
  isItemActive: (key: TourItemKey) => {
    const { items } = get();
    return items.find((item) => item.key === key)?.isActive ?? false;
  },
}));
